import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  INTEGRATION_CATEGORY,
  INTEGRATION_STATUS,
  metaFor,
  type Integration,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<Integration>[] = [
  {
    key: "name",
    header: "Integration",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1 max-w-xs">{row.description || "—"}</p>
      </div>
    ),
  },
  { key: "category", header: "Category", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.category}</span> },
  { key: "account", header: "Account", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.account || "—"}</span> },
  { key: "sync", header: "Last sync", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.last_sync)}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(INTEGRATION_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<Integration> = {
  table: "integrations",
  orderBy: "name",
  eyebrow: "Administration",
  title: "Integrations",
  subtitle: "The connected services that power payments, messaging, email, marketing, analytics and maps.",
  searchPlaceholder: "Search integrations…",
  searchKeys: ["name", "category", "description", "account"],
  filters: [
    { key: "category", label: "Category", options: options(INTEGRATION_CATEGORY) },
    { key: "status", label: "Status", options: optionsFrom(INTEGRATION_STATUS) },
  ],
  statusMap: INTEGRATION_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Integrations", value: rows.length, icon: "ri-plug-line", tone: "primary" },
    { label: "Connected", value: rows.filter((row) => row.status === "connected").length, icon: "ri-link", tone: "primary" },
    { label: "Available", value: rows.filter((row) => row.status === "available").length, icon: "ri-add-circle-line", tone: "secondary" },
    { label: "Needs attention", value: rows.filter((row) => row.status === "error" || row.status === "disabled").length, icon: "ri-error-warning-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "name", label: "Name" },
    { key: "category", label: "Category", type: "select", options: options(INTEGRATION_CATEGORY) },
    { key: "status", label: "Status", type: "select", options: optionsFrom(INTEGRATION_STATUS) },
    { key: "account", label: "Account" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  createFields: [
    { key: "name", label: "Name" },
    { key: "category", label: "Category", type: "select", options: options(INTEGRATION_CATEGORY) },
    { key: "status", label: "Status", type: "select", options: optionsFrom(INTEGRATION_STATUS) },
    { key: "account", label: "Account" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  newDefaults: { name: "", category: "Payments", description: "", status: "available", account: "" },
  emptyTitle: "No integrations yet",
  emptyMessage: "Connect the services you use for payments, messaging, email and analytics.",
  rowTitle: (row) => row.name,
};

export default function AdminIntegrations() {
  return <ResourceModule config={config} />;
}